import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import { GeckoVerticalWeldJSON } from 'types/assetModel/features/welds/geckoVerticalWeld'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

const { pi } = forecastMath

export type DomeWallAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.DomeWall
  height: number
  radius: number
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  features: Record<string, GeckoVerticalWeldJSON>
}

export const DomeWallAvailableFeatures: GeckoFeatureModelType[] = []

export const NewDomeWallAssetModelJSON: DomeWallAssetModelJSON = {
  name: 'New Containment Dome Wall',
  type: GeckoComponentModelType.DomeWall,
  height: 0,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
}

export class GeckoDomeWall extends GeckoEditableComponent {
  type: GeckoComponentModelType.DomeWall
  radius: GeckoMeasurement
  height: GeckoMeasurement
  sceneConfig = undefined
  componentBounds: GeckoComponentRect

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    height: GeckoMeasurement,
    componentBounds: GeckoComponentRect
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.DomeWall
    this.radius = radius
    this.height = height
    this.componentBounds = componentBounds
  }

  public static CreateFromJSON(id: string, json: DomeWallAssetModelJSON) {
    const name = json.name
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )
    const height = GeckoMeasurement.CreateFromValue(
      json.height,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    return new GeckoDomeWall(id, json, name, radius, height, componentBounds)
  }

  public asJSON(): DomeWallAssetModelJSON {
    return generateDomeWall(
      this.name,
      this.height,
      this.radius,
      this.json as DomeWallAssetModelJSON
    )
  }
}

const generateDomeWall = (
  name: string,
  height: GeckoMeasurement,
  radius: GeckoMeasurement,
  otherData: DomeWallAssetModelJSON = NewDomeWallAssetModelJSON
): DomeWallAssetModelJSON => {
  const tubeHeight = height
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tubeRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tubeCircumference = Number(
    (2 * pi * tubeRadius).toPrecision(assetModelPrecision)
  )

  return {
    name: name,
    type: GeckoComponentModelType.DomeWall,
    height: tubeHeight,
    radius: tubeRadius,
    scene_config: otherData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tubeCircumference,
      y_end: tubeHeight,
    },
    features: otherData.features,
  }
}

export const DomeWallAssetModelFormFields = (JSON: DomeWallAssetModelJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Containment Dome Wall Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: JSON.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },

    {
      dataKey: 'radius',
      label: 'Diameter',
      value: JSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const DomeWallJSONFromFormFields = (
  component: DomeWallAssetModelJSON,
  formFields: FormField[]
) => {
  return generateDomeWall(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    component
  )
}
